import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SignFieldsPalette from './SignFieldsPalette';
import SignMappingCanvas from './SignMappingCanvas';
import SignFieldPropsPanel from './SignFieldPropsPanel';
import SignSignerPanel from './SignSignerPanel';
import { KIND, mergeFields, splitFields } from './sign-overlays';

/**
 * Step 2 — Map fields. Four panes: documents | palette | canvas | properties.
 *
 * The document rail is what multi-document adds. Fields belong to a document,
 * not to the envelope, so the canvas only ever shows one document's boxes and
 * the rail is how you move between them — and, more usefully, how you notice
 * that page 2 of the schedule has nothing on it yet.
 *
 * Field state lives on the subject in two arrays (prefillFields, signerFields),
 * each entry carrying `documentId`. The canvas works on the merged list for the
 * ACTIVE document only; saving merges it back with the untouched fields of
 * every other document.
 *
 * THE PROPERTIES PANE HAS THREE STATES, which is what absorbed step 3:
 *
 *   a field is selected   → its properties, exactly as before
 *   a signer is selected  → their name, address and coverage  (was step 3)
 *   nothing is selected   → the request's own settings and the send checklist
 *
 * The third is the one that made the merge worth doing. That slot used to hold
 * "select a field" — an instruction, in the widest idle space on the screen —
 * and signing order and expiry, which are neither a field nor a signer, had
 * nowhere else to go.
 *
 * `selection` is one piece of state rather than two, because the three states
 * are exclusive and two booleans would let them disagree.
 */
export default function Step2MapFields({
  subject,
  board,
  boardSettings,
  documentUrl,
  onFieldsChange,
  onAddSigner,
  onRemoveSigner,
  onRenameSigner,
  onPatchSigner,
  onResolveSignerEmail,
  resolvingSigner,
  onCreateEmailColumn,
  onPatchRequest,
  mode = 'envelope',
}) {
  const documents = useMemo(() => subject.documents || [], [subject.documents]);
  const [activeDocumentId, setActiveDocumentId] = useState(documents[0]?.id ?? null);
  /** `{ kind: 'field', id }` | `{ kind: 'signer', index }` | null. */
  const [selection, setSelection] = useState(null);
  const selectedId = selection?.kind === 'field' ? selection.id : null;
  const selectFieldId = (id) => setSelection(id ? { kind: 'field', id } : null);
  const [armedChip, setArmedChip] = useState(null);
  const [activeSigner, setActiveSigner] = useState(0);
  const [pageDims, setPageDims] = useState({});
  // Rendered page size in CSS px, reported by the canvas. The palette needs it
  // to draw a drag ghost the size of the box the drop will create.
  const [pagePx, setPagePx] = useState(null);

  // Keep the active document valid as documents are added or removed.
  useEffect(() => {
    if (!documents.length) {
      setActiveDocumentId(null);
      return;
    }
    if (!documents.some((d) => d.id === activeDocumentId)) {
      setActiveDocumentId(documents[0].id);
    }
  }, [documents, activeDocumentId]);

  // Page sizes are per document; a switch must not size boxes against the
  // previous document's pages.
  useEffect(() => {
    setPageDims({});
    // Only a FIELD selection is document-scoped. A signer spans documents, and
    // clearing them on a document switch would close the panel mid-edit for
    // someone flipping through to see where that party still has to sign.
    setSelection((sel) => (sel?.kind === 'field' ? null : sel));
  }, [activeDocumentId]);

  const handlePageDims = useCallback((page, dims) => {
    setPageDims((prev) => {
      const current = prev[page];
      if (current && current.width === dims.width && current.height === dims.height) return prev;
      return { ...prev, [page]: dims };
    });
  }, []);

  const activeDocument = documents.find((d) => d.id === activeDocumentId) || null;

  const allFields = useMemo(
    () => mergeFields(subject.prefillFields, subject.signerFields),
    [subject.prefillFields, subject.signerFields],
  );
  /** Only this document's boxes reach the canvas. */
  const canvasFields = useMemo(
    () => allFields.filter((f) => f.documentId === activeDocumentId),
    [allFields, activeDocumentId],
  );

  const prefillColumns = useMemo(() => board?.prefillColumns || [], [board]);
  const columnTitles = useMemo(
    () => new Map(prefillColumns.map((c) => [c.id, c.title])),
    [prefillColumns],
  );
  const prefillColumnIds = useMemo(() => new Set(prefillColumns.map((c) => c.id)), [prefillColumns]);
  const placedColumnIds = useMemo(
    () => new Set((subject.prefillFields || []).map((f) => f.columnId)),
    [subject.prefillFields],
  );

  const selectedField = canvasFields.find((f) => f.id === selectedId) || null;
  const parties = subject.signers || subject.roles || [];

  /*
    A template has ROLES, not people — no address to set, and `onPatchSigner`
    is not passed. Falling back to the request panel there would be worse than
    it sounds: a template has no signing order, no expiry and nothing to send,
    so the panel would be three empty controls. The template editor keeps the
    field panel and its empty state.
  */
  const signerPanelAvailable = mode === 'envelope' && typeof onPatchSigner === 'function';
  const selectedSigner =
    signerPanelAvailable && selection?.kind === 'signer' ? parties[selection.index] ?? null : null;

  /*
    Which field has claimed each board column for its answer.

    Built across the WHOLE envelope, not just this document: two documents in
    one request both carrying a "Job title" field pointed at the same column is
    exactly the case that goes wrong quietly, and it is invisible while you are
    looking at one page. First claim wins, matching the writer.
  */
  const mappingClaims = useMemo(() => {
    const claims = new Map();
    for (const f of subject.signerFields || []) {
      if (f.mappingColumnId && !claims.has(f.mappingColumnId)) claims.set(f.mappingColumnId, f.id);
    }
    return claims;
  }, [subject.signerFields]);

  /** What the selected party signs, across the whole envelope — not this document. */
  const signerCoverage = useMemo(() => {
    if (selection?.kind !== 'signer') return { documents: 0, fields: 0, hasSignature: false };
    const mine = (subject.signerFields || []).filter((f) => f.signerIndex === selection.index);
    return {
      documents: new Set(mine.map((f) => f.documentId)).size,
      fields: mine.length,
      hasSignature: mine.some((f) => f.type === 'signature'),
    };
  }, [selection, subject.signerFields]);

  /** Merge this document's edited list back with every other document's. */
  const handleFields = (nextForDocument) => {
    const untouched = allFields.filter((f) => f.documentId !== activeDocumentId);
    onFieldsChange(splitFields([...untouched, ...nextForDocument]));
  };

  /**
   * Clear every placement on the DOCUMENT IN VIEW — signer boxes and item-data
   * boxes alike, since both are "fields I put on this page" to the person who
   * put them there.
   *
   * Scoped to this document by construction: `handleFields` replaces only the
   * active document's list and keeps every other document's untouched, so an
   * empty list here cannot reach a document you are not looking at.
   */
  const clearActiveDocument = () => {
    handleFields([]);
    setSelection((sel) => (sel?.kind === 'field' ? null : sel));
    setArmedChip(null);
  };

  const patchSelected = (patch) => {
    handleFields(canvasFields.map((f) => (f.id === selectedId ? { ...f, ...patch } : f)));
  };

  const removeSelected = () => {
    handleFields(canvasFields.filter((f) => f.id !== selectedId));
    setSelection(null);
  };

  // Picking a field owned by signer 2 makes signer 2 active, so the next drop
  // follows what the user is visibly working on.
  useEffect(() => {
    if (selectedField?.kind === KIND.SIGNER) setActiveSigner(selectedField.signerIndex);
  }, [selectedField]);

  // Signers can be removed — the active tab must not point past the end.
  useEffect(() => {
    setActiveSigner((a) => Math.min(a, Math.max(0, parties.length - 1)));
  }, [parties.length]);

  /** Per-document field counts for the rail — the "did I forget one" check. */
  const countFor = (documentId) => allFields.filter((f) => f.documentId === documentId).length;

  return (
    <div className='flex-1 flex overflow-hidden'>
      {/* ── Document rail ── */}
      <aside className='flex-shrink-0 w-40 bg-white border-r border-gray-200 overflow-y-auto'>
        <p className='px-3 pt-4 pb-2 text-[10px] font-semibold uppercase tracking-widest text-gray-400'>
          Documents
        </p>
        <div className='px-2 pb-3 space-y-1'>
          {documents.map((doc, i) => {
            const active = doc.id === activeDocumentId;
            const count = countFor(doc.id);
            return (
              <button
                key={doc.id}
                onClick={() => setActiveDocumentId(doc.id)}
                className={`w-full text-left px-2.5 py-2 rounded-md transition-colors ${
                  active ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <div className='flex items-baseline gap-1.5'>
                  <span className='text-[10px] font-mono opacity-60'>{i + 1}</span>
                  <span className={`text-xs truncate ${active ? 'font-semibold' : 'font-medium'}`}>
                    {doc.name}
                  </span>
                </div>
                <div className={`text-[10px] mt-0.5 ${count ? 'opacity-70' : 'text-amber-600'}`}>
                  {count ? `${count} field${count === 1 ? '' : 's'}` : 'no fields'}
                </div>
              </button>
            );
          })}
        </div>
      </aside>

      <SignFieldsPalette
        template={subject}
        prefillColumns={prefillColumns}
        activeSigner={activeSigner}
        /*
          One click, two jobs — and they are the same job. Picking a signer row
          has always decided who owns the next chip you drop; now it also fills
          the panel. Both mean "this party is the one I am working on", so they
          stay one action rather than a click and a separate affordance.
        */
        onSelectSigner={(i) => {
          setActiveSigner(i);
          setSelection({ kind: 'signer', index: i });
        }}
        onAddSigner={onAddSigner}
        onRemoveSigner={onRemoveSigner}
        onRenameSigner={onRenameSigner}
        placedColumnIds={placedColumnIds}
        armedChip={armedChip}
        onArmChip={setArmedChip}
        pagePx={pagePx}
        signingOrder={subject.signingOrder}
        /* Envelopes only. A template has no send, so it has no order to set —
           `orderMatters` in the palette keys off this being present. */
        onChangeSigningOrder={
          onPatchRequest ? (signingOrder) => onPatchRequest({ signingOrder }) : undefined
        }
      />

      <div
        className='flex-1 flex flex-col overflow-hidden'
        style={{
          background:
            'radial-gradient(circle, rgba(148,163,184,0.18) 1px, transparent 1px) 0 0 / 18px 18px, var(--color-bg-page, #F8FAFC)',
        }}
      >
        {activeDocument ? (
          <SignMappingCanvas
            key={activeDocument.id}
            fileUrl={documentUrl(activeDocument.id)}
            documentId={activeDocument.id}
            pageCount={activeDocument.pageCount || 1}
            fields={canvasFields}
            signers={parties}
            columnTitles={columnTitles}
            prefillColumnIds={prefillColumnIds}
            activeSigner={activeSigner}
            armedChip={armedChip}
            selectedId={selectedId}
            onChange={handleFields}
            onSelect={selectFieldId}
            onPlacedFromChip={() => setArmedChip(null)}
            onPageDims={handlePageDims}
            onRenderedSize={setPagePx}
            onClearDocument={clearActiveDocument}
            width={600}
          />
        ) : (
          <div className='flex-1 flex items-center justify-center text-sm text-gray-500'>
            Add a document on step 1 first.
          </div>
        )}
      </div>

      {selectedField ? (
        <SignFieldPropsPanel
          field={selectedField}
          signers={parties}
          columnTitles={columnTitles}
          prefillColumnIds={prefillColumnIds}
          mappingColumns={board?.mappingColumns || []}
          mappingClaims={mappingClaims}
          pagePt={pageDims[selectedField.page] || null}
          onChange={patchSelected}
          onRemove={removeSelected}
          onClose={() => setSelection(null)}
        />
      ) : selectedSigner ? (
        <SignSignerPanel
          signer={selectedSigner}
          signerIndex={selection.index}
          signerCount={parties.length}
          emailColumns={board?.emailColumns || []}
          documentCount={documents.length}
          fieldCount={signerCoverage.fields}
          documentsSigned={signerCoverage.documents}
          hasSignature={signerCoverage.hasSignature}
          resolving={resolvingSigner === selection.index}
          onPatch={(patch) => onPatchSigner(selection.index, patch)}
          onResolveFromColumn={(columnId) => onResolveSignerEmail(selection.index, columnId)}
          onCreateColumn={() => onCreateEmailColumn(selection.index)}
          onRemove={() => {
            onRemoveSigner(selection.index);
            setSelection(null);
          }}
          onClose={() => setSelection(null)}
        />
      ) : (
        /* Template mode. A template has no request to configure and no send to
           check, so the panel keeps the plain empty state it always had. */
        <SignFieldPropsPanel
          field={null}
          signers={parties}
          columnTitles={columnTitles}
          prefillColumnIds={prefillColumnIds}
          pagePt={null}
          onChange={patchSelected}
          onRemove={removeSelected}
          onClose={() => setSelection(null)}
        />
      )}
    </div>
  );
}
